import { useEffect, useState } from "react";
import { useAdminPanel } from "../context/admin-panel";
import { getFolio, Folio } from "@csc-dashboard/api-client";
import {
  AtButtonProps,
  AtTag,
  AtTagFolioStatus,
  AtTagNumberVariant,
  AtTagOrganizationStatus,
  AtTagType,
  CSCFooterDrawer,
  formatCurrency,
  formatDate,
  MlTableMessage,
  OrDrawer,
} from "@csc-dashboard/components-library";

const FORMAT_DATE = "D [de] MMMM [de] YYYY, H:mm [h]";

export const FolioDetailContainer = () => {
  const { tabParams, setCurrentTab } = useAdminPanel();
  const folioId = tabParams?.folioId as string;
  const [folio, setFolio] = useState<Folio>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCartDetail = async () => {
      try {
        setIsLoading(true);
        const { folio } = await getFolio({ id: folioId });
        setFolio(folio);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCartDetail();
  }, [folioId]);

  const buttons = [
    {
      text: "Regresar",
      onClick: () => {
        setCurrentTab("folios");
      },
    },
  ];

  return (
    <>
      <OrDrawer.Content>
        <div className="p-2">
          {isLoading ? (
            <LoadingContentSkeleton />
          ) : (
            <>
              {
                <div className="flex flex-col gap-6 text-surface-500">
                  <div className="flex flex-col gap-4">
                    <div className="flex gap-2 items-center">
                      <p className="font-semibold ">Folio</p>
                      <AtTag
                        type={AtTagType.NUMBER}
                        label={folioId}
                        variant={AtTagNumberVariant.FOLIO}
                      />
                      {folio?.status && (
                        <AtTag
                          type={AtTagType.FOLIO}
                          status={folio.status as AtTagFolioStatus}
                        />
                      )}
                    </div>
                    <div className="flex flex-col gap-1 ">
                      <p className="text-sm font-semibold ">Creado</p>
                      <p className="text-normal">
                        {formatDate(folio?.createdAt as string, FORMAT_DATE)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 ">
                      <p className="text-sm font-semibold">
                        Ultima modificación
                      </p>
                      <p className="text-normal">
                        {formatDate(folio?.modifiedAt as string, FORMAT_DATE)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 items-start">
                      <p className="text-sm font-semibold">Tienda</p>
                      {folio?.organization && (
                        <AtTag
                          type={AtTagType.ORGANIZATION}
                          status={
                            folio?.organization as AtTagOrganizationStatus
                          }
                        />
                      )}
                    </div>
                  </div>
                  <hr className="border-surface-75 border-t-1" />
                  {folio?.items?.length === 0 ? (
                    <MlTableMessage
                      darkerDescription
                      size="mini"
                      title="Sin artículos registrados"
                      description="Este folio fue atendido sin registrar artículos."
                    />
                  ) : (
                    <div className="flex flex-col">
                      <div className="flex flex-col p-2 border-b-2 border-surface-75">
                        <p className="text-sm">Productos</p>
                      </div>
                      {folio?.items?.map((item) => (
                        <div
                          key={item.id}
                          className="flex p-4 border-b-1 border-surface-75 gap-2"
                        >
                          <img
                            src={item.image?.src || ""}
                            alt={item.image?.alt || ""}
                            width={70}
                            height={90}
                            className="max-h-[90px] max-w-[70px] object-cover"
                          />
                          <div className="flex flex-col gap-2 text-sm">
                            <p className="font-semibold">{item.name}</p>
                            <p>Cantidad: {item.quantity}</p>
                            <p>
                              {getDiscountPercentage(
                                Number(item.price.original),
                                Number(item.price.discount),
                              )}
                              % de descuento
                            </p>
                            <p>
                              {formatCurrency(item.price.discount)} por artículo
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              }
            </>
          )}
        </div>
      </OrDrawer.Content>
      <OrDrawer.Footer>
        <CSCFooterDrawer
          mainButton={buttons[0] as AtButtonProps}
          secondaryButton={buttons[1]}
        />
      </OrDrawer.Footer>
    </>
  );
};

const getDiscountPercentage = (original: number, discount: number) => {
  if (original === discount) {
    return 0;
  }

  return Math.round(
    ((Number(original) - Number(discount)) / Number(original)) * 100,
  );
};

// Skeleton for all the container
const LoadingContentSkeleton = () => {
  return (
    <div className="flex flex-col gap-6 animate-pulse">
      {/* Header Section */}
      <div className="flex flex-col gap-4">
        {/* Folio Title and Tags */}
        <div className="flex gap-2 items-center">
          <div className="h-6 w-16 bg-surface-100 rounded" />
          <div className="h-6 w-20 bg-surface-100 rounded" />
          <div className="h-6 w-20 bg-surface-100 rounded" />
        </div>

        {/* Created Date */}
        <div className="flex flex-col gap-1">
          <div className="h-4 w-16 bg-surface-100 rounded" />
          <div className="h-5 w-48 bg-surface-100 rounded" />
        </div>

        {/* Modified Date */}
        <div className="flex flex-col gap-1">
          <div className="h-4 w-32 bg-surface-100 rounded" />
          <div className="h-5 w-48 bg-surface-100 rounded" />
        </div>

        {/* Store */}
        <div className="flex flex-col gap-1 items-start">
          <div className="h-4 w-16 bg-surface-100 rounded" />
          <div className="h-6 w-24 bg-surface-100 rounded" />
        </div>
      </div>

      <hr className="border-surface-75 border-t-1" />

      {/* Products Section */}
      <div className="flex flex-col">
        <div className="flex flex-col p-2 border-b-2 border-surface-75">
          <div className="h-4 w-20 bg-surface-100 rounded" />
        </div>

        {/* Product Items Skeleton */}
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="flex p-4 border-b-1 border-surface-75 gap-2"
          >
            <div className="w-[70px] h-[90px] bg-surface-100 rounded" />
            <div className="flex flex-col gap-2 flex-1">
              <div className="h-4 w-3/4 bg-surface-100 rounded" />
              <div className="h-4 w-24 bg-surface-100 rounded" />
              <div className="h-4 w-32 bg-surface-100 rounded" />
              <div className="h-4 w-28 bg-surface-100 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
